import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { AppLocale, DEFAULT_LOCALE, normalizeLocale, resolveStoredLocale } from './locale';
import enUSDictionary from './locales/en-US.json';
import esESDictionary from './locales/es-ES.json';
import ptBRDictionary from './locales/pt-BR.json';
import ptPTDictionary from './locales/pt-PT.json';

/**
 * Resource keys are the CANONICAL BCP-47 tags, hyphenated.
 *
 * This is the single most important detail in this module. i18next drops the
 * region when it cannot find a region-specific resource, so with the underscore
 * keys this app used to carry (`pt_br`, `es_es`, `en_us`) a `pt_pt` resolved to
 * the `pt` bundle — Brazilian Portuguese — and a resource keyed `pt_PT` was
 * never reachable at all. European Portuguese is only expressible in the
 * hyphenated form; see the probe recorded in `./locale.ts`.
 */
const resources = {
  'pt-BR' : { translation : { ...ptBRDictionary } },
  'pt-PT' : { translation : { ...ptPTDictionary } },
  'en-US' : { translation : { ...enUSDictionary } },
  'es-ES' : { translation : { ...esESDictionary } },
};

/**
 * `fallbackLng` as an OBJECT, so European Portuguese degrades through Brazilian
 * Portuguese before reaching the default.
 *
 * A key missing from pt-PT read in pt-BR is still Portuguese; read in the
 * default it might not be. The `default` entry is mandatory in the object
 * form — without it, a locale with no entry has NO fallback and i18next leaks
 * the raw key onto the screen.
 */
export const FALLBACK_LNG: Record<string, string[]> = {
  'pt-PT' : ['pt-BR', DEFAULT_LOCALE],
  default : [DEFAULT_LOCALE],
};

/**
 * The language this session opens in, in precedence order: the shared cookie,
 * then `?locale=`, then the device, then the default.
 *
 * The cookie outranks the query parameter because it is a choice the visitor
 * made — here or in another Ingresse application — while the parameter is only
 * a hint from whoever sent them here.
 *
 * NO `LanguageDetector`. The plugin used to be mounted with `caches: ['cookie']`
 * and its default `lookupCookie` of `i18next`, so it read and wrote a SECOND
 * locale cookie that nothing in this app ever consulted — the real source has
 * always been the shared `locale` cookie. Its detection was overwritten on
 * mount anyway. Two cookies disagreeing about the language is the divergence
 * this epic exists to remove.
 */
export function resolveInitialLocale(search: string): AppLocale {
  const stored = resolveStoredLocale();
  if (stored) return stored;

  const fromQuery = normalizeLocale(new URLSearchParams(search).get('locale'));
  if (fromQuery) return fromQuery;

  const fromDevice = (navigator.languages ?? [])
    .map((language) => normalizeLocale(language))
    .find((locale): locale is AppLocale => locale !== null);

  return fromDevice ?? DEFAULT_LOCALE;
}

/**
 * Boots the shared i18next instance.
 *
 * Resolving the language BEFORE `init` rather than in an effect is what keeps
 * the checkout from flashing the wrong language on first paint.
 */
export function initI18n(): typeof i18n {
  i18n.use(initReactI18next).init({
    debug : false,
    lng : resolveInitialLocale(typeof window === 'undefined' ? '' : window.location.search),
    fallbackLng : FALLBACK_LNG,
    interpolation : { escapeValue : false },
    resources,
  });

  return i18n;
}

export default i18n;
